<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('attributes.payment')"
    size="large"
    @close="$emit('close')"
  >
    <template #subtitle>
      <div class="text-base text-gray-500">
        {{
          $t('attributes.outstanding_balance') +
          ' ' +
          $h.formatCurrency(getRest, invoice.currency)
        }}
      </div>
    </template>
    <template #body>
      <PaymentModalWizard
        v-model:payment="payment"
        v-model:step="step"
        :place-holder-issuer="invoice.invoiceable.full_name"
        :place-holder-direct-debit="invoice.dossier?.concernedPerson?.full_name"
        :dossier-contacts="dossier_contacts"
        :currency="invoice.currency"
        :form-submitting="formSubmitting"
        :custom-payment-modes="custom_payment_modes"
        :has-dossier="!!invoice?.dossier"
        @submit:payment="submitForm"
      >
        <template #other-informations>
          <div class="col-start-1">
            <WalkmanButton
              v-if="invoice"
              v-model="payment.is_payment_deposit"
              :options="[
                { value: 1, label: $t('enums.yesNo.1') },
                { value: 0, label: $t('enums.yesNo.0') }
              ]"
              :label="$t('global.deposit_payment')"
              :edition-mode
            />
          </div>
          <div v-if="invoice.reference !== null" class="flex flex-col gap-2">
            <div>
              <h3 class="capitalize-first text-base font-semibold">
                {{ $t('attributes.invoice_status') }}
              </h3>
            </div>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div class="col-start-1">
                <SelectInvoiceStatuses
                  v-model="payment.next_invoice_status"
                  :label="$t('attributes.next_invoice_status')"
                  edition-mode
                />
              </div>
            </div>
          </div>
          <div
            v-if="
              invoice.dossier_id &&
              invoice.doc_role === 'ff' &&
              payment.next_invoice_status === 'PAI' &&
              invoice.dossier.status !== 'PAY'
            "
          >
            <div class="flex w-full flex-col">
              <h2
                class="capitalize-first text-xs font-medium"
                v-html="$t('global.change_dossier_status')"
              ></h2>
              <div class="col-start-1">
                <BaseShowEditRadio
                  v-model="next_dossier_status"
                  class="col-span-1"
                  :edition-mode="true"
                  size="extra-small"
                  name="change_status_dossier"
                  :options="[
                    { id: 1, name: $t('enums.yesNo.1') },
                    { id: 0, name: $t('enums.yesNo.0') }
                  ]"
                />
              </div>
            </div>
          </div>
        </template>
      </PaymentModalWizard>
    </template>
  </BaseCenteredModal>
  <ConfirmModal ref="paymentSyncWarning" />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import PaymentModalWizard from '@ui/modal/payment/components/PaymentModalWizard.vue'
import SelectInvoiceStatuses from '@c/InitialStateFormComponent/Enums/SelectInvoiceStatuses.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import DocTypes from '@/assets/enums/invoicing/DocTypes.js'

export default {
  name: 'PaymentInvoiceFormModal',
  components: {
    WalkmanButton,
    BaseShowEditRadio,
    SelectInvoiceStatuses,
    PaymentModalWizard,
    BaseCenteredModal,
    ConfirmModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    paymentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    editBank: {
      type: [Number],
      required: false,
      default: 0
    },
    invoice: {
      type: Object,
      required: true
    },
    paymentData: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  emits: ['close', 'update:is-modal-open'],
  data() {
    return {
      step: 0,
      formSubmitting: false,
      custom_payment_modes: [],
      dossier_contacts: [],
      originalAmount: null,
      payment: null,
      next_dossier_status: null
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    },
    getRest() {
      return this.invoice?.total_tvac - this.invoice?.total_paid
    },
    totalInvoiceReceived() {
      if (!this.invoice?.payments) return 0

      return this.invoice?.payments
        .filter((p) => p.payment_status === 'REC')
        .reduce((sum, p) => sum + p.amount, 0)
    }
  },
  watch: {
    isModalOpen(newValue) {
      if (newValue) {
        if (!this.paymentId) {
          this.processFetchNewPaymentAction({
            invoice_id: this.invoice.id
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
        } else {
          this.processFetchPaymentByIdAction({
            invoice_id: this.invoice.id,
            payment_id: this.paymentId
          }).then((response) => {
            this.handleFetchApiResponse(response)
          })
        }
      } else {
        this.payment = null
        this.step = 0
      }
    },
    'payment.amount'(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        /** For invoice with LIT status, we only allow manual status changes */
        if (
          this.invoice.status !== 'DRA' &&
          this.invoice.status !== 'LIT' &&
          this.invoice.doc_type !== DocTypes.PURCHASE_ORDER
        ) {
          let totalWithCurrent = parseFloat(this.totalInvoiceReceived)

          if (this.payment.payment_status === 'REC') {
            totalWithCurrent += parseFloat(this.payment.amount)
          }

          if (totalWithCurrent >= this.invoice.total_tvac) {
            this.payment.next_invoice_status = 'PAI'
          } else {
            this.payment.next_invoice_status = 'PP'
          }
        }
      }
    }
  },
  methods: {
    ...mapActions({
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID',
      processFetchNewPaymentAction: 'invoicing/fetchNewPayment',
      processStoreNewPaymentAction: 'invoicing/storeNewPayment',
      processFetchPaymentByIdAction: 'invoicing/fetchPaymentById',
      processUpdatePaymentByIdAction: 'invoicing/updatePaymentById',
      processFetchNewBankAction: 'invoicing/storeNewBank'
    }),
    handleFetchApiResponse(response) {
      this.payment = {
        ...response.payment,
        ...this.paymentData,
        payment_status:
          this.paymentData?.payment_status ?? response.payment.payment_status,
        payment_type: 'CRE',
        next_invoice_status: this.invoice.status
      }
      this.originalAmount = this.payment.amount
      this.custom_payment_modes = response.metadata.payment_modes ?? []
      this.dossier_contacts = response.metadata.dossier_contacts ?? []
      if (
        (this.$can('super-admin') || this.$can('manager')) &&
        this.custom_payment_modes.length > 0
      ) {
        this.custom_payment_modes.push({
          id: 'custom',
          denomination: {
            [this.$i18n.locale]: this.$t('global.predefined')?.replace(
              /^./,
              (c) => c.toUpperCase()
            )
          }
        })
        this.payment.payment_mode_id = this.payment.payment_mode_id ?? 'custom'
      }
      if (this.custom_payment_modes.length === 0) {
        this.payment.payment_mode_id = 'custom'
      }
    },
    async submitForm() {
      if (this.amountErrors?.length) return
      if (this.payment_modes?.length > 0 && !this.payment.payment_mode_id) {
        this.payment_mode_error = true
        return
      }

      if (this.invoice?.payments_sync_status === 'ENA') {
        // Held across the await on purpose: the confirmation is a sibling dialog, and
        // opening it makes the form modal report itself closed, which nulls
        // this.payment through the isModalOpen watcher. Without this snapshot the
        // submission below would post an empty payment.
        const pendingPayment = { ...this.payment }
        const pendingDossierStatus = this.next_dossier_status

        const confirmed = await this.$refs.paymentSyncWarning.show({
          title: this.$t('invoicing.payments_sync_interrupt_title'),
          message: this.$t('invoicing.payments_sync_interrupt_warning'),
          okButton: this.$t('global.yes')
        })

        // Restored on both paths: the state was wiped when the confirmation opened,
        // so refusing the interruption has to put the form back too. Returning
        // without restoring would close the whole form and lose what was typed —
        // the outcome of the most cautious click available.
        this.payment = pendingPayment
        this.next_dossier_status = pendingDossierStatus
        this.localIsModalOpen = true

        if (!confirmed) {
          return
        }
      }

      this.formSubmitting = true
      if (!this.paymentId) {
        this.processStoreNewPaymentAction({
          invoice_id: this.invoice.id,
          payment: this.payment,
          change_status_dossier: this.next_dossier_status
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_added_to_invoice'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.next_dossier_status = null
            this.bankType = 1
            this.payment_mode_error = false
          })
          .catch((_reason) => {
            this.formSubmitting = false
            this.$emit('update:is-modal-open', false)
          })
      } else {
        this.processUpdatePaymentByIdAction({
          invoice_id: this.invoice.id,
          payment_id: this.payment.id,
          payment: this.payment,
          change_status_dossier: this.change_status_dossier
        })
          .then((_response) => {
            this.$h.toastSuccess(this.$t('toasts.payment_updated'))
            this.localIsModalOpen = false
            this.formSubmitting = false
            this.change_status_dossier = null
            this.payment_mode_error = false
          })
          .catch((_reason) => {
            this.formSubmitting = false
          })
      }
    }
  }
}
</script>
